/**
 * PublicProfileSettings
 * Component for managing public advisor profile settings
 */

import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/shared/lib/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Badge,
} from "@/shared/components/ui";
import { LabelWithTooltip } from "@/shared/components/ui/Tooltip";
import {
  Globe,
  Eye,
  EyeOff,
  Save,
  AlertCircle,
  CheckCircle,
  Plus,
  X,
  ExternalLink,
  Clock,
  Award,
  Building2,
  MapPin,
  Users,
  Loader2,
} from "lucide-react";
import type {
  AdvisorPublicProfile,
  ContactPreferences,
  CreditType,
} from "@/shared/types";

const CREDIT_TYPE_LABELS: Record<CreditType, string> = {
  hipoteczny: "Hipoteczny",
  gotowkowy: "Gotówkowy",
  konsolidacyjny: "Konsolidacyjny",
};

const SUGGESTED_REGIONS = [
  "Warszawa",
  "Kraków",
  "Wrocław",
  "Poznań",
  "Gdańsk",
  "Łódź",
  "Katowice",
  "Lublin",
  "Mazowieckie",
  "Małopolskie",
  "Wielkopolskie",
  "Dolnośląskie",
  "Pomorskie",
  "Śląskie",
];

const SUGGESTED_TARGET_CLIENTS = [
  "Młode pary",
  "Przedsiębiorcy",
  "Freelancerzy",
  "Rodziny",
  "Seniorzy",
  "Inwestorzy",
  "Pierwsza nieruchomość",
  "Refinansowanie",
];

const SUGGESTED_BANKS = [
  "PKO BP",
  "Santander",
  "ING Bank Śląski",
  "mBank",
  "Pekao SA",
  "BNP Paribas",
  "Millennium",
  "Credit Agricole",
  "Alior Bank",
  "BOŚ Bank",
];

interface ProfileFormData {
  isPublic: boolean;
  slug: string;
  headline: string;
  bio: string;
  yearsOfExperience: number;
  clientsServed?: number;
  creditsFinalized?: number;
  specializations: CreditType[];
  targetClients: string[];
  regions: string[];
  certificates: Array<{
    name: string;
    issuedBy: string;
    year: number;
  }>;
  partnerBanks: string[];
}

interface ContactFormData {
  preferredContactMethod: "phone" | "email" | "both";
  availableHours: {
    weekdays: { from: string; to: string };
    saturday?: { from: string; to: string };
    sunday?: { from: string; to: string };
  };
  responseTime: string;
}

const initialProfileForm: ProfileFormData = {
  isPublic: false,
  slug: "",
  headline: "",
  bio: "",
  yearsOfExperience: 1,
  clientsServed: undefined,
  creditsFinalized: undefined,
  specializations: [],
  targetClients: [],
  regions: [],
  certificates: [],
  partnerBanks: [],
};

const initialContactForm: ContactFormData = {
  preferredContactMethod: "both",
  availableHours: {
    weekdays: { from: "09:00", to: "17:00" },
  },
  responseTime: "W ciągu 24h",
};

export function PublicProfileSettings() {
  const queryClient = useQueryClient();
  const [profileForm, setProfileForm] =
    useState<ProfileFormData>(initialProfileForm);
  const [contactForm, setContactForm] =
    useState<ContactFormData>(initialContactForm);
  const [newCertificate, setNewCertificate] = useState({
    name: "",
    issuedBy: "",
    year: new Date().getFullYear(),
  });
  const [slugError, setSlugError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Fetch profile data
  const { data: profileData, isLoading: profileLoading } = useQuery({
    queryKey: ["publicProfile"],
    queryFn: () => api.getPublicProfile(),
  });

  const { data: contactData, isLoading: contactLoading } = useQuery({
    queryKey: ["contactPreferences"],
    queryFn: () => api.getContactPreferences(),
  });

  // Update form when data loads
  useEffect(() => {
    if (profileData?.publicProfile) {
      const p = profileData.publicProfile as AdvisorPublicProfile;
      setProfileForm({
        isPublic: p.isPublic || false,
        slug: p.slug || "",
        headline: p.headline || "",
        bio: p.bio || "",
        yearsOfExperience: p.yearsOfExperience || 1,
        clientsServed: p.clientsServed,
        creditsFinalized: p.creditsFinalized,
        specializations: p.specializations || [],
        targetClients: p.targetClients || [],
        regions: p.regions || [],
        certificates: p.certificates || [],
        partnerBanks: p.partnerBanks || [],
      });
    }
  }, [profileData]);

  useEffect(() => {
    if (contactData?.contactPreferences) {
      const c = contactData.contactPreferences as ContactPreferences;
      setContactForm({
        preferredContactMethod: c.preferredContactMethod || "both",
        availableHours: c.availableHours || {
          weekdays: { from: "09:00", to: "17:00" },
        },
        responseTime: c.responseTime || "W ciągu 24h",
      });
    }
  }, [contactData]);

  // Mutations
  const profileMutation = useMutation({
    mutationFn: (data: Partial<ProfileFormData>) => api.updatePublicProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["publicProfile"] });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    },
  });

  const contactMutation = useMutation({
    mutationFn: (data: Partial<ContactFormData>) =>
      api.updateContactPreferences(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contactPreferences"] });
    },
  });

  // Slug validation
  const validateSlug = (slug: string) => {
    if (!slug) return;
    const slugRegex = /^[a-z0-9-]+$/;
    if (!slugRegex.test(slug)) {
      setSlugError("URL może zawierać tylko małe litery, cyfry i myślniki");
      return;
    }
    setSlugError(null);
  };

  const handleSlugChange = (value: string) => {
    const normalized = value
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
    setProfileForm((f) => ({ ...f, slug: normalized }));
    validateSlug(normalized);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (slugError) return;

    profileMutation.mutate(profileForm);
    contactMutation.mutate(contactForm);
  };

  const toggleSpecialization = (type: CreditType) => {
    setProfileForm((f) => ({
      ...f,
      specializations: f.specializations.includes(type)
        ? f.specializations.filter((t) => t !== type)
        : [...f.specializations, type],
    }));
  };

  const addItem = (
    field: "targetClients" | "regions" | "partnerBanks",
    value: string
  ) => {
    if (!value.trim()) return;
    if (profileForm[field].includes(value)) return;
    setProfileForm((f) => ({
      ...f,
      [field]: [...f[field], value],
    }));
  };

  const removeItem = (
    field: "targetClients" | "regions" | "partnerBanks",
    value: string
  ) => {
    setProfileForm((f) => ({
      ...f,
      [field]: f[field].filter((v) => v !== value),
    }));
  };

  const addCertificate = () => {
    if (!newCertificate.name || !newCertificate.issuedBy) return;
    setProfileForm((f) => ({
      ...f,
      certificates: [...f.certificates, { ...newCertificate }],
    }));
    setNewCertificate({
      name: "",
      issuedBy: "",
      year: new Date().getFullYear(),
    });
  };

  const removeCertificate = (index: number) => {
    setProfileForm((f) => ({
      ...f,
      certificates: f.certificates.filter((_, i) => i !== index),
    }));
  };

  const isLoading = profileLoading || contactLoading;
  const isPending = profileMutation.isPending || contactMutation.isPending;
  const error = profileMutation.error || contactMutation.error;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Status & Visibility */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Profil publiczny
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {success && (
            <div className="p-3 bg-success-50 border border-success-200 rounded-lg flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-success-600" />
              <p className="text-sm text-success-700">
                Profil został zaktualizowany
              </p>
            </div>
          )}

          {error && (
            <div className="p-3 bg-error-50 border border-error-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0" />
              <p className="text-sm text-error-700">
                {(error as Error).message}
              </p>
            </div>
          )}

          {/* Public toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              {profileForm.isPublic ? (
                <Eye className="w-5 h-5 text-success-600" />
              ) : (
                <EyeOff className="w-5 h-5 text-gray-400" />
              )}
              <div>
                <p className="font-medium">
                  {profileForm.isPublic
                    ? "Profil widoczny publicznie"
                    : "Profil ukryty"}
                </p>
                <p className="text-sm text-gray-600">
                  {profileForm.isPublic
                    ? "Klienci mogą znaleźć Twój profil"
                    : "Tylko Ty widzisz swój profil"}
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant={profileForm.isPublic ? "secondary" : "primary"}
              size="sm"
              onClick={() =>
                setProfileForm((f) => ({ ...f, isPublic: !f.isPublic }))
              }
            >
              {profileForm.isPublic ? "Ukryj" : "Opublikuj"}
            </Button>
          </div>

          {/* Slug */}
          <div>
            <Input
              label={
                <LabelWithTooltip
                  label="Adres URL profilu"
                  tooltip="Unikalny adres, pod którym klienci znajdą Twój profil. Używaj tylko małych liter, cyfr i myślników."
                />
              }
              value={profileForm.slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              placeholder="jan-kowalski"
              error={slugError || undefined}
            />
            {profileForm.slug && !slugError && (
              <p className="mt-1 text-sm text-gray-500">
                Twój profil będzie dostępny pod adresem:{" "}
                <span className="font-mono text-primary-600">
                  /doradca/{profileForm.slug}
                </span>
              </p>
            )}
          </div>

          {profileForm.isPublic && profileForm.slug && (
            <a
              href={`/doradca/${profileForm.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:underline"
            >
              <ExternalLink className="w-4 h-4" />
              Zobacz podgląd profilu
            </a>
          )}
        </CardContent>
      </Card>

      {/* Basic Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Informacje podstawowe
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            label="Nagłówek profilu"
            value={profileForm.headline}
            onChange={(e) =>
              setProfileForm((f) => ({ ...f, headline: e.target.value }))
            }
            placeholder="np. Ekspert kredytów hipotecznych z 10-letnim doświadczeniem"
            maxLength={100}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Opis (bio)
            </label>
            <textarea
              value={profileForm.bio}
              onChange={(e) =>
                setProfileForm((f) => ({ ...f, bio: e.target.value }))
              }
              placeholder="Opisz swoje doświadczenie, podejście do klienta, specjalizacje..."
              rows={4}
              maxLength={1000}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              {profileForm.bio.length}/1000 znaków
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Input
              label="Lata doświadczenia"
              type="number"
              min={0}
              max={50}
              value={profileForm.yearsOfExperience}
              onChange={(e) =>
                setProfileForm((f) => ({
                  ...f,
                  yearsOfExperience: parseInt(e.target.value) || 0,
                }))
              }
            />
            <Input
              label="Obsłużonych klientów"
              type="number"
              min={0}
              value={profileForm.clientsServed || ""}
              onChange={(e) =>
                setProfileForm((f) => ({
                  ...f,
                  clientsServed: e.target.value
                    ? parseInt(e.target.value)
                    : undefined,
                }))
              }
              placeholder="np. 500"
            />
            <Input
              label="Sfinalizowanych kredytów"
              type="number"
              min={0}
              value={profileForm.creditsFinalized || ""}
              onChange={(e) =>
                setProfileForm((f) => ({
                  ...f,
                  creditsFinalized: e.target.value
                    ? parseInt(e.target.value)
                    : undefined,
                }))
              }
              placeholder="np. 350"
            />
          </div>
        </CardContent>
      </Card>

      {/* Specializations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="w-5 h-5" />
            Specjalizacje
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Typy kredytów
            </label>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(CREDIT_TYPE_LABELS) as CreditType[]).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => toggleSpecialization(type)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    profileForm.specializations.includes(type)
                      ? "bg-primary-100 text-primary-700 border-2 border-primary-500"
                      : "bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200"
                  }`}
                >
                  {CREDIT_TYPE_LABELS[type]}
                </button>
              ))}
            </div>
          </div>

          {/* Target Clients */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Grupy docelowe klientów
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {profileForm.targetClients.map((client) => (
                <Badge key={client} variant="default" className="gap-1">
                  {client}
                  <button
                    type="button"
                    onClick={() => removeItem("targetClients", client)}
                    className="hover:text-error-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex flex-wrap gap-1">
              {SUGGESTED_TARGET_CLIENTS.filter(
                (c) => !profileForm.targetClients.includes(c)
              ).map((client) => (
                <button
                  key={client}
                  type="button"
                  onClick={() => addItem("targetClients", client)}
                  className="text-xs px-2 py-1 bg-gray-50 text-gray-600 rounded hover:bg-gray-100"
                >
                  + {client}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Regions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Regiony działania
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-2">
            {profileForm.regions.map((region) => (
              <Badge key={region} variant="default" className="gap-1">
                {region}
                <button
                  type="button"
                  onClick={() => removeItem("regions", region)}
                  className="hover:text-error-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
          <div className="flex flex-wrap gap-1">
            {SUGGESTED_REGIONS.filter(
              (r) => !profileForm.regions.includes(r)
            ).map((region) => (
              <button
                key={region}
                type="button"
                onClick={() => addItem("regions", region)}
                className="text-xs px-2 py-1 bg-gray-50 text-gray-600 rounded hover:bg-gray-100"
              >
                + {region}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Partner Banks */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Współpracujące banki
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-2">
            {profileForm.partnerBanks.map((bank) => (
              <Badge key={bank} variant="default" className="gap-1">
                {bank}
                <button
                  type="button"
                  onClick={() => removeItem("partnerBanks", bank)}
                  className="hover:text-error-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
          <div className="flex flex-wrap gap-1">
            {SUGGESTED_BANKS.filter(
              (b) => !profileForm.partnerBanks.includes(b)
            ).map((bank) => (
              <button
                key={bank}
                type="button"
                onClick={() => addItem("partnerBanks", bank)}
                className="text-xs px-2 py-1 bg-gray-50 text-gray-600 rounded hover:bg-gray-100"
              >
                + {bank}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Certificates */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="w-5 h-5" />
            Certyfikaty i uprawnienia
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {profileForm.certificates.length > 0 && (
            <div className="space-y-2">
              {profileForm.certificates.map((cert, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900">{cert.name}</p>
                    <p className="text-sm text-gray-600">
                      {cert.issuedBy} ({cert.year})
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeCertificate(index)}
                    className="text-gray-400 hover:text-error-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <Input
              label="Nazwa certyfikatu"
              value={newCertificate.name}
              onChange={(e) =>
                setNewCertificate((c) => ({ ...c, name: e.target.value }))
              }
              placeholder="np. KNF Pośrednik"
            />
            <Input
              label="Wydawca"
              value={newCertificate.issuedBy}
              onChange={(e) =>
                setNewCertificate((c) => ({ ...c, issuedBy: e.target.value }))
              }
              placeholder="np. KNF"
            />
            <Input
              label="Rok"
              type="number"
              min={1990}
              max={new Date().getFullYear()}
              value={newCertificate.year}
              onChange={(e) =>
                setNewCertificate((c) => ({
                  ...c,
                  year: parseInt(e.target.value) || new Date().getFullYear(),
                }))
              }
            />
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={addCertificate}
            disabled={!newCertificate.name || !newCertificate.issuedBy}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            Dodaj certyfikat
          </Button>
        </CardContent>
      </Card>

      {/* Contact Preferences */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Preferencje kontaktu
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preferowany sposób kontaktu
            </label>
            <div className="flex gap-2">
              {(["phone", "email", "both"] as const).map((method) => (
                <button
                  key={method}
                  type="button"
                  onClick={() =>
                    setContactForm((f) => ({
                      ...f,
                      preferredContactMethod: method,
                    }))
                  }
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    contactForm.preferredContactMethod === method
                      ? "bg-primary-100 text-primary-700 border-2 border-primary-500"
                      : "bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200"
                  }`}
                >
                  {method === "phone"
                    ? "Telefon"
                    : method === "email"
                      ? "Email"
                      : "Oba"}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Godziny dostępności (dni robocze)"
              type="text"
              value={`${contactForm.availableHours.weekdays.from} - ${contactForm.availableHours.weekdays.to}`}
              onChange={(e) => {
                const [from, to] = e.target.value.split(" - ");
                setContactForm((f) => ({
                  ...f,
                  availableHours: {
                    ...f.availableHours,
                    weekdays: { from: from || "09:00", to: to || "17:00" },
                  },
                }));
              }}
              placeholder="09:00 - 17:00"
            />
            <Input
              label="Czas odpowiedzi"
              value={contactForm.responseTime}
              onChange={(e) =>
                setContactForm((f) => ({ ...f, responseTime: e.target.value }))
              }
              placeholder="np. W ciągu 24h"
            />
          </div>
        </CardContent>
      </Card>

      {/* Submit Button */}
      <div className="flex justify-end">
        <Button
          type="submit"
          isLoading={isPending}
          leftIcon={<Save className="w-4 h-4" />}
          size="lg"
        >
          Zapisz profil publiczny
        </Button>
      </div>
    </form>
  );
}
